// eslint-disable-next-line
import { types, flow, Instance } from 'mobx-state-tree';
// eslint-disable-next-line
import { AxiosResponse } from 'axios';
import axios from 'server';

const { model, array, maybeNull, number, string, union, identifierNumber, optional } = types;

export const LDAPConnectionModel = model('LDAPConnectionModel', {
  host: optional(string, ''),
  domain: optional(maybeNull(string), ''),
  port: optional(string, ''),
  securityType: optional(maybeNull(string), ''),
  username: optional(string, ''),
  password: optional(string, ''),
  baseDn: optional(string, ''),
  active: optional(string, ''),
  ldapType: optional(string, ''),
  createdAt: maybeNull(string),
  type: maybeNull(string),
  name: string,
  id: identifierNumber,
});

export const SyncHistoryModel = model('SyncHistoryModel', {
  lastSyncTime: maybeNull(string),
  message: string,
  status: string,
});

export const MappingSettingEntityModel = model('MappingSettingEntityModel', {
  id: optional(number, 0),
  client_id: optional(number, 0),
  entity: optional(string, ''),
  label: string,
  key: string,
  ldapType: optional(maybeNull(string), null),
  defaultValue: maybeNull(union(array(string), array(number), string, number)), //@TODO: need to remove array(number) and number
  type: optional(string, ''),
  hidden: optional(number, 0),
});

export const MappingSettingModel = model('MappingSettingModel', {
  people: array(MappingSettingEntityModel),
  group: array(MappingSettingEntityModel),
  department: array(MappingSettingEntityModel),
});

export const MappingEntityModel = model('MappingEntityModel', {
  sourseLabel: maybeNull(string),
  sourseKey: string,
  destKey: maybeNull(string),
});

export const MappingModel = model('MappingModel', {
  people: maybeNull(array(MappingEntityModel)),
  group: maybeNull(array(MappingEntityModel)),
  department: maybeNull(array(MappingEntityModel)),
});

export const SyncSheduleModel = model('SyncSheduleModel', {
  id: number,
  ldapType: string,
  name: string,
  numberOfDays: number,
  period: string,
  selected: number,
});

export const SyncStatusModel = model('SyncStatusModel', {
  numberOfRecordsSynchronizationCompleted: number,
  numberOfRecordsSynchronizationInProgress: number,
  totalNumberOfRecordsToSynchronize: number,
  ezidCloudLdapHostName: string,
  customerCloudLdapHostName: string,
});

export type ILDAPConnectionModel = Instance<typeof LDAPConnectionModel>;

export const ImportStore = model('ImportStore', {
  ldapConnection: LDAPConnectionModel,
  ldapAllConnections: array(LDAPConnectionModel),
  ldapLastSync: SyncHistoryModel,
  mappingSettings: MappingSettingModel,
  mapping: MappingModel,
  syncShedule: array(SyncSheduleModel),
  syncStatus: SyncStatusModel,
  step: '',
  type: '',
})
  .volatile(() => ({}))
  .actions((self) => ({
    stepNumberData() {
      return self.step;
    },
    normalizeStore({ data }: AxiosResponse) {
      const mappingSettings = data.data?.mappingSettings;
      if (Array.isArray(mappingSettings)) {
        const grouped = mappingSettings.reduce((acc: any, item: any) => {
          const entity = item.entity || 'people';
          if (!acc[entity]) acc[entity] = [];
          acc[entity].push({
            ...item,
            label: item.destLabel || '',
            key: item.destKey || '',
            hidden: item.hidden ? 1 : 0,
          });
          return acc;
        }, {});
        self.mappingSettings = {
          people: grouped.people || [],
          group: grouped.group || [],
          department: grouped.department || [],
        };
      } else {
        self.mappingSettings = mappingSettings || { people: [], group: [], department: [] };
      }
      const mapping = data.data?.mapping;
      if (Array.isArray(mapping)) {
        const groupedMapping = mapping.reduce((acc: any, item: any) => {
          const entity = item.entity || 'people';
          if (!acc[entity]) acc[entity] = [];
          acc[entity].push({
            sourseLabel: item.sourseLabel || null,
            sourseKey: item.sourseKey || '',
            destKey: item.destKey || null,
          });
          return acc;
        }, {});
        self.mapping = {
          people: groupedMapping.people || null,
          group: groupedMapping.group || null,
          department: groupedMapping.department || null,
        };
      } else {
        self.mapping = {
          people: mapping?.people || null,
          group: mapping?.group || null,
          department: mapping?.department || null,
        };
      }
      return self;
    },
    normalizeStoreLDIF({ data }: AxiosResponse) {
      const mappingSettings = data.data?.mappingSettings;
      if (Array.isArray(mappingSettings)) {
        const grouped = mappingSettings.reduce((acc: any, item: any) => {
          const entity = item.entity || 'people';
          if (!acc[entity]) acc[entity] = [];
          acc[entity].push({
            ...item,
            label: item.destLabel || '',
            key: item.destKey || '',
            hidden: item.hidden ? 1 : 0,
          });
          return acc;
        }, {});
        self.mappingSettings = {
          people: grouped.people || [],
          group: grouped.group || [],
          department: grouped.department || [],
        };
      } else {
        self.mappingSettings = mappingSettings || { people: [], group: [], department: [] };
      }
      const mapping = data.data?.mapping;
      if (Array.isArray(mapping)) {
        const groupedMapping = mapping.reduce((acc: any, item: any) => {
          const entity = item.entity || 'people';
          if (!acc[entity]) acc[entity] = [];
          acc[entity].push({
            sourseLabel: item.sourseLabel || null,
            sourseKey: item.sourseKey || '',
            destKey: item.destKey || null,
          });
          return acc;
        }, {});
        self.mapping = {
          people: groupedMapping.people || null,
          group: groupedMapping.group || null,
          department: groupedMapping.department || null,
        };
      } else {
        self.mapping = {
          people: mapping?.people || null,
          group: mapping?.group || null,
          department: mapping?.department || null,
        };
      }
      self.type = data.data?.type || '';
      return data;
    },
    normalizeLdapLastSync({ data }: AxiosResponse) {
      self.ldapLastSync = data.data
        ? data.data
        : {
            lastSyncTime: null,
            message: '',
            status: '',
          };

      if (self.ldapLastSync?.lastSyncTime !== null) {
        self.step = 'one';
      }
      return self.ldapLastSync;
    },
    normalizeUpdateShedule({ data }: AxiosResponse) {
      self.ldapLastSync = {
        lastSyncTime: data.data?.lastSyncTime,
        message: data.data?.message,
        status: '0',
      };
      return self.ldapLastSync;
    },
    normalizeSheduleOptions({ data }: AxiosResponse) {
      self.syncShedule = data.data;
      return self;
    },
    normalizeConnectOptions({ data }: AxiosResponse) {
      self.ldapAllConnections = data.data;

      const activeConnection = data.data.find((i: Instance<typeof LDAPConnectionModel>) => i.active === '1');
      if (activeConnection) {
        self.ldapConnection = activeConnection;
      }
      return self.ldapAllConnections;
    },
    normalizeLdapGetSyncProcess({ data }: AxiosResponse) {
      self.syncStatus = data?.data;
      self.ldapLastSync = data?.data;
      return self;
    },
    updateLdapConnectionHost(value: string) {
      self.ldapConnection.host = value;
    },
    updateLdapConnectionPort(value: string) {
      self.ldapConnection.port = value;
    },
    updateLdapConnectionSecurityType(value: string) {
      self.ldapConnection.securityType = value;
    },
    updateLdapConnectionUsername(value: string) {
      self.ldapConnection.username = value;
    },
    updateLdapConnectionPassword(value: string) {
      self.ldapConnection.password = value;
    },
    updateLdapConnectionBaseDn(value: string) {
      self.ldapConnection.baseDn = value;
    },
    updateSyncSchedule(id: number, status = 1) {
      self.syncShedule.forEach((i, key) => {
        self.syncShedule[key].selected = i.id === id ? status : 0;
      });
      return self.syncShedule;
    },
    updateDepartmentMapping(sourseKey: string, destKey: string) {
      if (!self.mapping.department) return null;
      const departments = [...self.mapping.department];
      departments.map((i: Instance<typeof MappingEntityModel>) => {
        if (i.sourseKey === sourseKey) {
          i.destKey = destKey;
        }
        return i;
      });
      self.mapping.department.replace(departments);
      return self.mapping.department;
    },
    updateGroupMapping(sourseKey: string, destKey: string) {
      if (!self.mapping.group) return null;
      const groups = [...self.mapping.group];
      groups.map((i: Instance<typeof MappingEntityModel>) => {
        if (i.sourseKey === sourseKey) {
          i.destKey = destKey;
        }
        return i;
      });
      self.mapping.group.replace(groups);
      return self.mapping.group;
    },
    updatePeopleMapping(sourseKey: string, destKey: string) {
      if (!self.mapping.people) return null;
      const peoples = [...self.mapping.people];
      peoples.map((i: Instance<typeof MappingEntityModel>) => {
        if (i.sourseKey === sourseKey) {
          i.destKey = destKey;
        }
        return i;
      });
      self.mapping.people.replace(peoples);
      return self.mapping.people;
    },
  }))
  .actions((self) => ({
    setStep: (step: string) => {
      self.step = step;
      return self.step;
    },
    selectLdapConnection: (selectedConnection: Instance<typeof LDAPConnectionModel>) => {
      self.ldapConnection = { ...selectedConnection, active: '1' };
      return self.ldapConnection;
    },
    ldapSyncHistory: flow(function* () {
      yield axios
        .get(`admin/ldap/last-ldap-sync`)
        .then(self.normalizeLdapLastSync)
        .catch((error) => {
          throw new Error(error.response.data.error);
        });
    }),
    ldapGetConnectOptions: flow(function* () {
      const result = yield axios
        .get(`ldap/files`)
        .then(self.normalizeConnectOptions)
        .catch((error) => {
          throw new Error(error.response.data.error);
        });
      return result;
    }),

    getLdapAttributePreferences: flow(function* () {
      return yield axios
        .get(`app-attributes/ldap`)
        .then((res) => {
          return res.data;
        })
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
    }),
    ldapGetSheduleOptions: flow(function* () {
      yield axios
        .get(`admin/ldap/get-sync-schedule-data`)
        .then(self.normalizeSheduleOptions)
        .catch((error) => {
          throw new Error(error.response.data.error);
        });
    }),
    ldapUpdateShedule: flow(function* () {
      yield axios
        .post(`admin/ldap/save-sync-schedule-data`, { data: self.syncShedule })
        .then(self.normalizeUpdateShedule)
        .catch((error) => {
          throw new Error(error.response.data.data);
        });
    }),
    ldapPauseResume: flow(function* () {
      yield axios
        .post(`admin/pauseResumeLdapSync`)
        .then()
        .catch((error) => {
          throw new Error(error);
        });
    }),
    ldapConnect: flow(function* () {
      const result = yield axios
        .post(`admin/ldap/save-connect-data`, self.ldapConnection)
        .then(self.normalizeStore)
        .catch((error) => {
          throw new Error(error.response.data.message);
        });
      return result;
    }),
    ldapNewConnect: flow(function* (data: any) {
      const result = yield axios
        .post(`admin/ldap/save-new-connect-data`, data)
        .then()
        .catch((error) => {
          throw new Error(error.response.data.message);
        });
      return result;
    }),
    ldapUpdateMapping: flow(function* () {
      yield axios
        .put(`ldap/mappings/update`, { mapping: self.mapping })
        //.then(self.normalizeUpdateMapping)
        .catch((error) => {
          const errorMessage =
            error.response?.data?.data?.message ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          throw new Error(errorMessage);
        });
    }),
    ldapGetSyncProcess: flow(function* () {
      const result = yield axios
        .get(`admin/ldap/get-sync-progress`)
        .then(self.normalizeLdapGetSyncProcess)
        .catch((error) => {
          throw new Error(error.response.data.error);
        });
      return result;
    }),
    sendCsvFile: flow(function* (data: any) {
      const formData = new FormData();
      formData.append('ldapFile', data.ldapFile);
      const result = yield axios
        .post(`ldap/connections/import`, formData)
        .then(self.normalizeStoreLDIF)
        .catch((error) => {
          const errorMessage = error.response?.data?.data?.message || error.response?.data?.message || error.message;
          throw new Error(errorMessage);
        });
      return result;
    }),
    sendLdifFile: flow(function* (data: any) {
      const formData = new FormData();
      formData.append('ldapFile', data.ldapFile);
      const result = yield axios
        .post(`admin/ldap/process-ldip`, formData)

        .then(self.normalizeStoreLDIF)

        .catch((error) => {
          throw error.response;
        });
      return result;
    }),
  }));
